import React, {FC, ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {useQuery} from "@apollo/client";
import {useDrag, useDrop} from "react-dnd";
import classnames from "classnames/bind";
import {v4 as uuidv4} from "uuid";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";

import {EmptyComponent} from "../../types";
import {
	Category,
	Collection,
	DELETE_POST,
	MovePostVars,
	REFRESH_SHUFFLE_AI,
	REMOVE_CATEGORY_FROM_COLLECTION,
	REORDER_CATEGORIES,
	UPDATE_POST,
	Post,
	PostKeyword,
	postServices,
	PostServices,
	useMyUser,
	OPENGRAPH_FROM_URL,
	OpenGraph,
	services,
} from "../../data";
import {
	Button,
	DropdownButton,
	EmojiPickerInput,
	FileUpload,
	InputHeader,
	Separator,
	Text,
	required,
} from "../../components/input";
import {Arrow, Icon} from "../../components/images";
import {IconToggleGroup} from "../../components/toggle";
import {useMutationToast, useToast} from "../../toast";
import {
	useConfirmRevokeModal,
	ReferralCampaignModal,
	useNewModal as useModal,
	useConfirmDeleteModal,
} from "../../modals";
import {useKeywordsModal} from "../../modals/collections/keywords";
import {useMediaLibraryModal} from "../../modals/media/media-library";
import {post as postApi} from "../../api";
import {useDirtyCopy} from "../../dirty-copy";
import {ShuffleContentModal} from "../../modals/shuffle-content/shuffle-content";
import {useConfirmChangeModal} from "./change-modal";
import {Badge} from "../../components/badge";
import {Span2, Span3} from "../../components/text";
import {useDatePickerModal} from "../../modals/schedule";
import {getPostEmv, PostServiceType} from "../../data/collection";
import {GET_ORG_VIDEO, Media, UPLOAD_ORG_VIDEO, inflateUploadedImage} from "../../data/media";
import {OpengraphMedia} from "../../components/opengraph";
import {MentionsText} from "../../components/input/mentions-text";
import {isURL} from "../../utils/text";
import {useStateRef} from "../../state-ref";
import {Loading} from "../../components/loading";
import {Pill} from "../../components/pill";

import styles from "./post.module.scss";
const bStyles = classnames.bind(styles);

const PostDragType = "post";
const CategoryDragType = "category";

interface PostDragObject {
	id: number;
}

export interface PostListProps {
	categoryId?: number;
	collectionId: number;
	posts: Post[];
	onDelete: (idx: number) => void;
	movePost: (variables: MovePostVars) => void;
	disabled: boolean;
}

interface PostItemProps extends Omit<PostListProps, "posts"> {
	post: Post;
	idx: number;
	afterId?: number;
	startOpen?: boolean;
}

interface PostCategoryProps extends EmptyComponent {
	collection: Collection;
	category: Category;
	index: number;
	movePost: (variables: MovePostVars) => void;
	onMove: (id?: number, idx?: number) => void;
	onDelete: (idx: number) => void;
	disabled: boolean;
}

interface PostCategoryListProps extends EmptyComponent {
	categories: Category[];
	collection: Collection;
	onDelete: (idx: number) => void;
	movePost: (variables: MovePostVars) => void;
	disabled: boolean;
}

const iconMapper = {general: "email"};
const toggleOptions = postServices.map(s => ({value: s, icon: iconMapper[s] || s}));

export const PostCategoryList: FC<PostCategoryListProps> = ({
	categories,
	collection,
	movePost,
	onDelete,
	disabled,
}) => {
	const [reorderCategories] = useMutationToast(REORDER_CATEGORIES);
	const [order, setOrder] = useState<number[]>();
	const handleMove = useCallback(
		(id?: number, idx?: number) => {
			if (id === undefined) {
				if (order !== undefined)
					reorderCategories({variables: {id: collection.id, categoryIds: order}}).then(() =>
						setOrder(undefined)
					);
				return;
			}
			setOrder(c => {
				if (idx === undefined) return undefined;
				const ret = (c ?? collection.categories ?? []).filter(i => i !== id);
				ret.splice(idx, 0, id);
				return ret;
			});
		},
		[collection, order, reorderCategories]
	);
	return (
		<div>
			{(order ?? collection.categories ?? []).map((cat, i) => {
				const category = categories.find(c => c.id === cat);
				if (!category) return <></>;
				return (
					<PostCategory
						key={cat}
						index={i}
						category={category}
						collection={collection}
						onMove={handleMove}
						onDelete={onDelete}
						movePost={movePost}
						disabled={disabled}
					/>
				);
			})}
		</div>
	);
};

export const PostCategory = ({
	category,
	collection,
	index,
	movePost,
	onDelete,
	onMove,
	disabled,
}: PostCategoryProps): ReactElement => {
	const [removeCategory] = useMutationToast(REMOVE_CATEGORY_FROM_COLLECTION);
	const handleRemove = useCallback(
		() => !disabled && removeCategory({variables: {id: collection.id, categoryId: category.id}}),
		[category.id, collection, removeCategory, disabled]
	);
	const [style, drag, preview] = useDrag(
		() => ({
			type: CategoryDragType,
			item: {id: category.id},
			collect: monitor => ({opacity: monitor.isDragging() ? 0 : 1}),
			end: (item, monitor) => {
				if (!monitor.didDrop()) onMove(category.id);
			},
		}),
		[category.id, onMove]
	);
	const [, drop] = useDrop(
		() => ({
			accept: CategoryDragType,
			drop: () => onMove(),
			hover({id}: {id: number}) {
				if (id !== category.id) onMove(id, index);
			},
		}),
		[category.id, index, onMove]
	);
	return (
		<div ref={node => preview(drop(node))} className="space" style={style}>
			<div className={bStyles("category", "space", disabled && "disabled")}>
				<Icon
					icon="drag"
					color="grey"
					height={16}
					className={[styles.draggable, disabled && styles.disabled]}
					ref={disabled ? null : drag}
				/>
				<Span2>{category.name}</Span2>
				<Icon icon="close" color="blue" className={disabled && styles.disabled} onClick={handleRemove} />
			</div>
			<PostList
				disabled={disabled}
				categoryId={category.id}
				posts={collection.posts.filter(p => p.categoryId === category.id)}
				onDelete={onDelete}
				collectionId={collection.id}
				movePost={movePost}
			/>
		</div>
	);
};

const emptyPostPlaceholder = "Write a comment or paste a link to share";

export const PostList = ({
	categoryId,
	collectionId: id,
	movePost,
	posts,
	disabled,
	...props
}: PostListProps): ReactElement => {
	const [over, drop] = useDrop<PostDragObject, unknown, boolean>(
		() => ({
			accept: PostDragType,
			drop: item => movePost({id, postId: item.id, categoryId}),
			collect: monitor => monitor.canDrop() && monitor.isOver(),
			canDrop: item => item.id !== posts[0]?.id,
		}),
		[categoryId, id, movePost, posts]
	);
	const [initialPostIds] = useState(posts?.map(post => post.id));

	return (
		<div>
			<div ref={disabled ? null : drop} className={styles.dropZone}>
				{over && <div className={styles.over} />}
			</div>
			{posts.map((p, i) => (
				<PostItem
					{...props}
					disabled={disabled}
					post={p}
					key={p.id}
					idx={i}
					collectionId={id}
					movePost={movePost}
					afterId={i < posts.length - 1 ? posts[i + 1].id : undefined}
					startOpen={!initialPostIds?.includes(p.id)}
				/>
			))}
		</div>
	);
};

const removeTypename = (obj: OpenGraph & {__typename?: string}) => {
	const cpy = {...obj};
	delete cpy.__typename;
	return cpy;
};

const PostItem = ({
	afterId,
	collectionId: id,
	idx,
	movePost,
	onDelete,
	post,
	disabled,
	startOpen,
}: PostItemProps): ReactElement => {
	const [orgVideoId, setOrgVideoId] = useState<number>();
	const me = useMyUser();
	const isAIEnabled = me.org.options.ai;
	const [deletePost, {loading: deleting}] = useMutationToast(DELETE_POST);
	const [perNetwork, setPerNetwork, perNetworkRef] = useStateRef(
		Object.values({...post.opengraphs, general: null, __typename: null}).filter(e => e !== null).length > 0
	);
	const [network, setNetwork, networkRef] = useStateRef<PostServices>(services[0]);
	const activeNetwork = perNetwork ? network : "general";
	const [refreshSocialShuffle] = useMutationToast(REFRESH_SHUFFLE_AI);
	const [updatePost, {loading: updatingPost}] = useMutationToast(UPDATE_POST);
	const [opengraphFromURL, {loading: loadingURL}] = useMutationToast(OPENGRAPH_FROM_URL);
	const [uploadOrgVideo, {loading: uploading}] = useMutationToast(UPLOAD_ORG_VIDEO);
	const {data, startPolling, stopPolling} = useQuery(GET_ORG_VIDEO, {
		variables: {id: orgVideoId},
		skip: !orgVideoId,
	});
	const onUpdate = useCallback(
		({val, discard, changes, ref}) => {
			const opengraphDeletions = {};
			const localOpengraphs = Object.keys(changes?.opengraphs ?? {}).reduce((acc, service) => {
				if (Object.keys(changes?.opengraphs?.[service]).length !== 0) {
					acc[service] = changes?.opengraphs?.[service];
					return acc;
				}
				if (Object.keys(val?.opengraphs?.[service] ?? {}).length !== 0) {
					opengraphDeletions[service] = true;
				}
				return acc;
			}, {});

			return updatePost({
				variables: {
					id,
					postId: val.id,
					changes: {
						...changes,
						...(changes?.keywords ? {keywords: changes.keywords.map(k => k.id)} : {}),
						opengraphs: localOpengraphs,
					},
					deletions: {
						expiresAt: changes && "expiresAt" in changes && changes.expiresAt === undefined,
						opengraphs: opengraphDeletions,
					},
				},
				onCompleted: () => {
					if (!isEqual(ref.current, changes)) return;
					discard();
				},
			});
		},
		[id, updatePost]
	);

	const textareaRef = React.useRef<HTMLTextAreaElement>(null);
	const {val, update, flush} = useDirtyCopy(post, {debounce: 2000, onUpdate});
	const updateActive = useCallback(
		(ogUpdate: Partial<OpenGraph>, baseUpdate: Partial<Post> = {}) => {
			const activeNetwork = perNetworkRef.current ? networkRef.current : "general";
			update((cur, changes) => ({
				...baseUpdate,
				opengraphs: {
					...(changes?.opengraphs ?? {}),
					[activeNetwork]: {
						...(cur.opengraphs[activeNetwork] || activeNetwork === "general"
							? {}
							: removeTypename(cur.opengraphs.general)),
						...changes?.opengraphs?.[activeNetwork],
						...ogUpdate,
					},
				},
			}));
		},
		[networkRef, perNetworkRef, update]
	);

	const opengraph = val.opengraphs[activeNetwork] ?? val.opengraphs.general;
	const isEMVEnabled = me.org.options.emvQueueEnabled;
	const toast = useToast();
	const [file, setFile] = useState<File>();
	const handleMediaUpdate = useCallback(
		(selectedMedia: Media) =>
			updateActive({
				image: selectedMedia.type === "image" ? selectedMedia.url : selectedMedia.thumbnailUrl,
				video: selectedMedia.type === "video" ? selectedMedia.url : undefined,
			}),
		[updateActive]
	);

	const mediaLibraryModal = useMediaLibraryModal({
		onConfirm: handleMediaUpdate,
	});
	const handleKeywordsUpdate = useCallback(
		(keywords: PostKeyword[]) =>
			update({
				keywords: keywords.map(k => ({
					id: k.id,
					value: k.value,
					keyword: k.keyword,
					global: k.global,
				})),
			}),
		[update]
	);
	const handleReferralCampaignUpdate = useCallback((url: string) => update({url}), [update]);
	const {modal, open: openReferral} = useModal({});
	const keywordsModal = useKeywordsModal({
		onConfirm: handleKeywordsUpdate,
		selectedKeys: val.keywords,
	});
	const handleUpdateShuffledComments = useCallback(
		(shuffledComments, shuffledImages) =>
			update((cur, changes) => ({
				shuffledComments:
					shuffledComments?.map(c => ({
						isAIGenerated: c.isAIGenerated,
						openGraph: {comment: c.openGraph.comment},
					})) ?? [],
				shuffledImages: shuffledImages?.map(i => ({image: i})) ?? [],
				opengraphs: {
					...changes?.opengraphs,
				},
			})),
		[update]
	);

	const {open: openShuffleContent, modal: shuffleContentModal} = useModal({size: "large"});

	const [open, setOpen] = useState(!!startOpen);
	const [over, drop] = useDrop<PostDragObject, unknown, boolean>(
		() => ({
			accept: PostDragType,
			drop: ({id: postId}) => movePost({id, postId, afterPostId: post.id}),
			collect: monitor => monitor.canDrop() && monitor.isOver(),
			canDrop: ({id}) => id !== post.id && id !== afterId,
		}),
		[afterId, id, movePost, post.id]
	);
	const [isDragging, drag, preview] = useDrag<PostDragObject>(
		() => ({
			type: PostDragType,
			item: {id: post.id},
			collect: monitor => monitor.isDragging(),
		}),
		[post.id]
	);
	const socialShuffleCommentsNumber = useMemo(() => val.shuffledComments.length, [val]);
	const handleAIUpdate = useCallback(
		({discard, val: aiChat}) =>
			refreshSocialShuffle({
				variables: {
					aiChat: {
						id: aiChat.id,
						message: aiChat.message,
						service: aiChat.service,
						prompt: opengraph.comment,
					},
				},
				onCompleted: ({refreshSocialShuffle: {message}}) => {
					discard();
					updateActive({comment: message});
				},
			}),
		[opengraph.comment, refreshSocialShuffle, updateActive]
	);
	const {update: updateAIChat, dirty: dirtyAIChat} = useDirtyCopy(
		{
			message: "",
			prompt: "",
			id: uuidv4(),
			service: activeNetwork.toUpperCase(),
		},
		{
			debounce: 500,
			onUpdate: handleAIUpdate,
		}
	);

	useEffect(() => {
		if (!open || disabled) return;

		textareaRef?.current?.focus();
	}, [disabled, open]);

	useEffect(() => {
		if (!file) return;

		const [type] = file.type.split("/");

		if (type === "image") {
			const formData = new FormData();
			formData.append(type, file);
			formData.append("name", type);
			postApi("/images/image-list", formData, {}, false)
				.then(data => {
					const newImage = inflateUploadedImage(data ?? {});
					updateActive({image: newImage.url});
				})
				.catch(error => toast({color: "red", text: error, icon: "warning"}))
				.finally(() => {
					setFile(undefined);
				});
		} else {
			uploadOrgVideo({
				variables: {file},
				onCompleted: data => {
					if (data?.uploadOrgVideo?.id) {
						setOrgVideoId(data.uploadOrgVideo.id);
						startPolling(5000);
					}
				},
			});
		}
	}, [file, startPolling, toast, update, updateActive, uploadOrgVideo]);

	useEffect(() => {
		if (!data?.orgVideo?.url || data?.orgVideo?.status !== "COMPLETE") return;

		stopPolling();
		setOrgVideoId(undefined);
		updateActive({video: data.orgVideo.url});
	}, [data, stopPolling, updateActive]);

	const handleDelete = (close: () => void) =>
		deletePost({
			variables: {id, postId: post.id},
			onCompleted: () => {
				close();
				onDelete(idx);
			},
		});

	const handleEmojiChange = (emoji: string) => {
		const pos = textareaRef?.current?.selectionStart ?? 0;

		updateActive({
			comment: `${opengraph.comment?.slice(0, pos)}${emoji}${opengraph.comment?.slice(pos)}`,
		});
		const focusInput = () => {
			textareaRef?.current?.focus();
			textareaRef?.current?.setSelectionRange(pos + emoji.length, pos + emoji.length);
		};

		setTimeout(focusInput, 0);
	};

	const {open: confirmRevoke} = useConfirmRevokeModal({
		what: "post",
		onConfirm: handleDelete,
		confirming: deleting,
	});

	const {open: confirmDelete} = useConfirmDeleteModal({
		what: "post",
		onDelete: handleDelete,
		deleting: deleting,
	});

	const getEmvValue = useMemo(
		() =>
			new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
			}).format(getPostEmv(val)),
		[val]
	);

	const {open: addExpiration} = useDatePickerModal({
		type: "Post",
		value: val.expiresAt ?? dayjs(),
		min: dayjs(),
		onConfirm: (value, close) => {
			update({expiresAt: value});
			close();
		},
	});

	const handleChange = useCallback(
		(v: string) => {
			if (!val.url && isURL(v)) {
				const url = v.trim();
				opengraphFromURL({variables: {url}}).then(({data}) => {
					if (!data.opengraphFromURL) return;
					const opengraph = removeTypename({...data.opengraphFromURL});

					updateActive(opengraph, {url});
				});
			} else {
				updateActive({comment: v});
			}
		},
		[opengraphFromURL, updateActive, val.url]
	);

	const resetToGeneral = useCallback(
		close => {
			close();
			setPerNetwork(false);
			if (services.filter(s => Object.keys(val.opengraphs[s] ?? {}).length !== 0).length !== 0) {
				update(() => ({
					opengraphs: {
						facebook: {},
						twitter: {},
						linkedin: {},
						instagram: {},
					},
				}));
			}
		},
		[setPerNetwork, val, update]
	);

	const resetAndRemove = useCallback(
		close => {
			setPerNetwork(false);
			update({
				opengraphs: {
					general: {
						title: "",
						image: null,
						description: "",
					},
					facebook: {},
					twitter: {},
					linkedin: {},
					instagram: {},
				},
				url: null,
			});
			close();
		},
		[setPerNetwork, update]
	);

	const resetShuffleContent = useCallback(
		close => {
			setPerNetwork(true);
			update({shuffledComments: []});
			setTimeout(() => flush().then(() => close()), 0);
		},
		[setPerNetwork, flush, update]
	);

	const confirmDeleteModal = useConfirmChangeModal({
		confirmColor: "pink",
		confirmText: "Delete Changes",
		onConfirm: resetToGeneral,
	});

	const confirmResetShuffleModal = useConfirmChangeModal({
		confirmColor: "pink",
		confirmText: "Delete Shuffle Content",
		onConfirm: resetShuffleContent,
	});

	const confirmResetAndRemove = useConfirmChangeModal({
		confirmColor: "pink",
		confirmText: "Remove URL",
		confirming: updatingPost,
		onConfirm: resetAndRemove,
	});

	const toggleCustomizePerNetwork = useCallback(
		close => {
			close();
			if (perNetwork) {
				confirmDeleteModal.open();
			} else {
				if (socialShuffleCommentsNumber > 0) {
					confirmResetShuffleModal.open();
				} else {
					setPerNetwork(c => !c);
				}
			}
		},
		[confirmDeleteModal, confirmResetShuffleModal, socialShuffleCommentsNumber, perNetwork, setPerNetwork]
	);

	const removeURL = useCallback(() => {
		update({
			opengraphs: {
				general: {
					title: "",
					image: null,
					video: null,
					description: "",
				},
			},
			url: null,
		});
	}, [update]);

	const onRemoveAll = useCallback(() => {
		if (perNetwork) {
			confirmResetAndRemove.open();
			return;
		}
		removeURL();
	}, [removeURL, perNetwork, confirmResetAndRemove]);

	const hasOpengraphText = val.url && ["facebook", "general"].includes(activeNetwork);
	const hasOpengraphMedia = !!(opengraph.image || opengraph.video);
	const hasUrl = val.url;
	return (
		<>
			<div className={styles.postItem}>
				<div ref={node => preview(node)} className={bStyles("postHeader", {isDragging})}>
					<Icon
						icon="drag"
						height={16}
						color="grey"
						ref={disabled ? null : drag}
						className={[styles.draggable, disabled && styles.disabled]}
					/>
					<div className={styles.content}>
						{perNetwork && open && (
							<IconToggleGroup value={network} options={toggleOptions} onChange={setNetwork} />
						)}
						<div className={bStyles("previewContainer", "space")}>
							{!open && <OpengraphMedia openGraph={opengraph} height={40} width={80} />}
							{disabled ||
								(!open && (
									<Span3 trim={1}>
										{opengraph.comment?.length ? opengraph.comment : emptyPostPlaceholder}
									</Span3>
								))}
						</div>
						{perNetwork && val.url && open && (
							<Pill
								outline
								textClassName={styles.postUrl}
								disabled
								color="blue"
								text={val.url}
								onDelete={confirmResetAndRemove.open}
							/>
						)}
					</div>
					<Arrow direction={open ? "up" : "down"} onClick={() => setOpen(c => !c)} />
				</div>
				{open && (
					<div className={styles.postBody}>
						<MentionsText
							ref={textareaRef}
							type="textarea"
							placeholder={emptyPostPlaceholder}
							onChange={handleChange}
							value={opengraph.comment ?? ""}
							errorText="You must enter a caption."
							{...(opengraph.comment?.length
								? {
										info: `Characters: ${opengraph.comment.length}`,
								  }
								: {})}
							validate={required}
							bare
							disabled={disabled}
							loading={loadingURL}
							service={PostServiceType[network || "general"]}
						/>
						{(hasOpengraphText || hasOpengraphMedia || loadingURL) && (
							<>
								<div className={bStyles("imageAndDescriptionContainer", {open, loadingOgMedia: loadingURL})}>
									{loadingURL && <Loading position="center" />}

									<OpengraphMedia
										className={bStyles("opengraphMedia", {ogMediaCut: hasUrl})}
										openGraph={opengraph}
										showControls
									/>
									{!loadingURL && !perNetwork && (
										<Icon icon="close" className={styles.closeIcon} onClick={onRemoveAll} />
									)}
									{hasUrl && (
										<div className={styles.ogMetadataContainer}>
											{val.url && <div className={styles.ogMetadataUrl}>{val.url}</div>}
											{!loadingURL && (
												<Text
													bold
													value={opengraph.title ?? ""}
													placeholder="Add a title"
													disabled={disabled}
													onChange={v => updateActive({title: v})}
													bare
												/>
											)}
											{hasOpengraphText && (
												<div className={styles.description}>
													<Text
														bare
														className={styles.description}
														value={opengraph.description ?? ""}
														type="textarea"
														minRows={3}
														placeholder="Add a description."
														disabled={disabled}
														onChange={v => updateActive({description: v})}
													/>
												</div>
											)}
										</div>
									)}
								</div>
							</>
						)}
						<InputHeader>
							<>
								{!disabled && (
									<>
										<FileUpload
											iconSize={20}
											onChange={setFile}
											type={["image", "video"]}
											value={file}
											onlyIcon
											disabled={disabled}
											loading={uploading || !!orgVideoId}
										/>
										<Button
											icon="photo-library"
											border={false}
											invert
											onClick={mediaLibraryModal.open}
											disabled={disabled}
										/>
										<Separator />
										<EmojiPickerInput
											value={opengraph.video || ""}
											onChange={handleEmojiChange}
											disabled={disabled}
										/>
										{isAIEnabled && (
											<Button
												loading={dirtyAIChat}
												invert
												border={false}
												className={[(opengraph.comment ?? "").length <= 3 && styles.disabledRobotIcon]}
												icon="robot"
												onClick={() => {
													updateAIChat({prompt: opengraph.comment});
												}}
												disabled={disabled}
											/>
										)}
										<Separator />
										<DropdownButton
											options={[
												{label: "Add Expiration", onClick: addExpiration},
												{
													label: "Remove Expiration Time",
													onClick: () => update({expiresAt: undefined}),
													hidden: !val.expiresAt,
												},
												{label: "Referral & Campaign Tracking", onClick: openReferral, hidden: !val.url},
												{
													label: "Customize Per Network",
													onClick: toggleCustomizePerNetwork,
													icon: "switch",
													selected: perNetwork,
													hidden: !val.url,
												},
											]}
											value="More"
											invert
											arrow
											border={false}
											disabled={disabled}
										/>
									</>
								)}

								{val.expiresAt && (
									<Span3 color="grey">Expires {val.expiresAt.formatAs("longDateAndTime")}</Span3>
								)}
							</>
							<>
								{val.url && (
									<Button
										color="blue"
										invert
										border={false}
										icon="shuffle"
										value={disabled ? "View Shuffle Content" : "Shuffle Content"}
										onClick={openShuffleContent}
										disabled={perNetwork}
									/>
								)}
								{socialShuffleCommentsNumber > 0 && (
									<Badge text={socialShuffleCommentsNumber} color="blue" size="small" />
								)}
							</>
						</InputHeader>
						<Separator horizontal />
						<InputHeader>
							<>
								{val.url && (
									<Button
										color="black"
										invert
										disabled={!isEMVEnabled || disabled}
										border={false}
										icon="information"
										value={`EMV: ${getEmvValue}`}
										onClick={keywordsModal.open}
									/>
								)}
							</>
							<Button
								color="black"
								invert
								border={false}
								icon="delete"
								value={disabled ? "Revoke" : "Delete"}
								onClick={disabled ? confirmRevoke : confirmDelete}
							/>
						</InputHeader>
					</div>
				)}
			</div>
			<div ref={!disabled ? drop : null} className={styles.dropZone}>
				{over && <div className={styles.over} />}
			</div>
			<ReferralCampaignModal url={val.url ?? ""} modal={modal} onConfirm={handleReferralCampaignUpdate} />
			<ShuffleContentModal
				modal={shuffleContentModal}
				post={val}
				onConfirm={handleUpdateShuffledComments}
				disabled={disabled}
			/>
		</>
	);
};
